// ═══════════════════════════════════════════════════════════════════
//  modules/sanctions/SanctionsListe.js
//  Recherche par pseudo, tri par date, filtre par modérateur — plus
//  les filtres de niveau, motif et état.
// ═══════════════════════════════════════════════════════════════════

import { useCallback, useEffect, useState } from "react";
import { useIsMobile } from "../../socle/hooks";
import { THEME } from "../../socle/theme";
import { dateCourte, dureeLisible, NIVEAU_STYLE, STATUT_STYLE, tempsRestant } from "./constantes";

const LIMITE = 50;

export function SanctionsListe({ api, bareme, onOuvrir, onJoueur }) {
  const isMobile = useIsMobile();
  const [data, setData] = useState(null);
  const [erreur, setErreur] = useState(null);
  const [offset, setOffset] = useState(0);
  const [f, setF] = useState({ pseudo: "", niveau: "", motif: "", moderateur: "", statut: "", tri: "recent", hors_bareme: "" });

  const charger = useCallback(async () => {
    try { setData(await api.liste({ ...f, limit: LIMITE, offset })); setErreur(null); }
    catch (e) { setErreur(e.message); }
  }, [api, f, offset]);

  useEffect(() => { charger(); }, [charger]);

  const champ = { background: THEME.surface, border: `1px solid ${THEME.border}`, color: THEME.text, borderRadius: 8, padding: "7px 10px", fontSize: 13, fontFamily: "inherit" };
  const maj = (cle, valeur) => { setOffset(0); setF((x) => ({ ...x, [cle]: valeur })); };

  const motifsDuNiveau = () => {
    if (!bareme) return [];
    if (f.niveau) return Object.entries(bareme.bareme[f.niveau] || {});
    return Object.values(bareme.bareme).flatMap((m) => Object.entries(m));
  };

  return (
    <div>
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 14 }}>
        <input placeholder="Rechercher un pseudo…" value={f.pseudo}
          onChange={(e) => maj("pseudo", e.target.value)} style={{ ...champ, minWidth: 200, flex: isMobile ? "1 1 100%" : "0 1 240px" }} />
        <select value={f.niveau} onChange={(e) => { maj("niveau", e.target.value); maj("motif", ""); }} style={champ}>
          <option value="">Tous les niveaux</option>
          {Object.entries(NIVEAU_STYLE).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
        </select>
        <select value={f.motif} onChange={(e) => maj("motif", e.target.value)} style={champ}>
          <option value="">Tous les motifs</option>
          {motifsDuNiveau().map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
        </select>
        <select value={f.moderateur} onChange={(e) => maj("moderateur", e.target.value)} style={champ}>
          <option value="">Tous les modérateurs</option>
          {(data ? data.moderateurs : []).map((m) => <option key={m} value={m}>{m}</option>)}
        </select>
        <select value={f.statut} onChange={(e) => maj("statut", e.target.value)} style={champ}>
          <option value="">Tous les états</option>
          {Object.entries(STATUT_STYLE).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
        </select>
        <select value={f.tri} onChange={(e) => maj("tri", e.target.value)} style={champ}>
          <option value="recent">Plus récentes d'abord</option>
          <option value="ancien">Plus anciennes d'abord</option>
        </select>
        <button onClick={() => maj("hors_bareme", f.hors_bareme ? "" : "1")}
          style={{ ...champ, cursor: "pointer", color: f.hors_bareme ? THEME.amber : THEME.textMuted, borderColor: f.hors_bareme ? THEME.amber : THEME.border, fontWeight: 700 }}>
          Hors barème
        </button>
      </div>

      {erreur && <div style={{ color: THEME.red, marginBottom: 12 }}>{erreur}</div>}
      {!data && <div style={{ color: THEME.textMuted }}>Chargement…</div>}

      {data && data.sanctions.length === 0 && (
        <div style={{ background: THEME.panel, border: `1px dashed ${THEME.border}`, borderRadius: 14, padding: 40, textAlign: "center", color: THEME.textMuted }}>
          Aucune sanction ne correspond à ces filtres.
        </div>
      )}

      {data && data.sanctions.map((s) => {
        const nv = NIVEAU_STYLE[s.niveau] || NIVEAU_STYLE.kick;
        const st = STATUT_STYLE[s.statut] || STATUT_STYLE.expiree;
        const libelle = ((bareme && bareme.bareme[s.niveau]) || {})[s.motif]
          ? bareme.bareme[s.niveau][s.motif].label : s.motif;
        return (
          <div key={s.id} onClick={() => onOuvrir(s.id)}
            style={{ background: THEME.panel, border: `1px solid ${THEME.border}`, borderLeft: `3px solid ${nv.color}`, borderRadius: 10, padding: "11px 14px", marginBottom: 8, cursor: "pointer" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 9, flexWrap: "wrap" }}>
              <span style={{ background: nv.bg, color: nv.color, borderRadius: 5, padding: "1px 8px", fontSize: 11, fontWeight: 800 }}>{nv.icone} {nv.label}</span>
              <button onClick={(e) => { e.stopPropagation(); onJoueur(s.pseudo); }}
                style={{ background: "transparent", border: "none", color: THEME.gold, fontWeight: 700, fontSize: 14, cursor: "pointer", padding: 0, fontFamily: "inherit" }}>
                {s.pseudo}
              </button>
              <span style={{ color: THEME.text, fontSize: 13 }}>{libelle}</span>
              <span style={{ color: THEME.textMuted, fontSize: 12 }}>{dureeLisible(s.duree_min)}</span>
              {s.hors_bareme === 1 && <span style={{ background: THEME.amberDim, color: THEME.amber, borderRadius: 5, padding: "1px 7px", fontSize: 10, fontWeight: 700 }}>hors barème</span>}
              <span style={{ background: st.bg, color: st.color, borderRadius: 5, padding: "1px 7px", fontSize: 10, fontWeight: 700 }}>
                {st.label}{s.statut === "active" && s.expire_le ? ` · ${tempsRestant(s.expire_le)}` : ""}
              </span>
              <span style={{ marginLeft: "auto", color: THEME.textDim, fontSize: 11 }}>
                {dateCourte(s.created_at)} · {s.moderateur_pseudo}
              </span>
            </div>
            {s.raison_publique && (
              <div style={{ color: THEME.textMuted, fontSize: 12.5, marginTop: 4, fontStyle: "italic" }}>« {s.raison_publique} »</div>
            )}
          </div>
        );
      })}

      {data && data.total > LIMITE && (
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 12 }}>
          <button disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - LIMITE))}
            style={{ ...champ, cursor: offset === 0 ? "not-allowed" : "pointer", opacity: offset === 0 ? 0.4 : 1 }}>← Préc.</button>
          <span style={{ color: THEME.textMuted, fontSize: 12 }}>
            {offset + 1} – {Math.min(offset + LIMITE, data.total)} sur {data.total}
          </span>
          <button disabled={offset + LIMITE >= data.total} onClick={() => setOffset(offset + LIMITE)}
            style={{ ...champ, cursor: offset + LIMITE >= data.total ? "not-allowed" : "pointer", opacity: offset + LIMITE >= data.total ? 0.4 : 1 }}>Suiv. →</button>
        </div>
      )}
    </div>
  );
}
